import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import LanguageSwitcher from '../i18n/LanguageSwitcher';
import { useFavicon } from '../utils/useFavicon';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #FAF8F5;
    color: #2C2C2C;
    -webkit-font-smoothing: antialiased;
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .auth-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: radial-gradient(ellipse 80% 60% at 50% 30%, #FFF0E8 0%, #FAF8F5 70%);
  }
  .auth-logo {
    /* #238 — text wordmark replaced by the full-lockup SVG.
       Height tuned so the wordmark sits at ~42px cap-height — close to
       the previous 1.25rem text but with the icon + subtitle. */
    display: block;
    text-align: center;
    margin-bottom: 32px;
    text-decoration: none;
    animation: fadeInUp 0.6s ease-out forwards;
  }
  .auth-logo img {
    height: 64px;
    width: auto;
    max-width: 90vw;
    display: inline-block;
  }
  .auth-card {
    background: #fff;
    border-radius: 20px;
    padding: 48px 44px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    animation: fadeInUp 0.6s ease-out 0.1s both;
  }
  .auth-title {
    font-size: 1.6rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin-bottom: 32px;
    color: #1A1A1A;
  }
  .form-group {
    margin-bottom: 20px;
  }
  .form-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #444;
    margin-bottom: 7px;
  }
  .form-input {
    width: 100%;
    padding: 14px 16px;
    font-family: inherit;
    font-size: 1rem;
    border: 1.5px solid #E0DDD8;
    border-radius: 10px;
    background: #FDFCFB;
    color: #2C2C2C;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    -webkit-appearance: none;
  }
  .form-input:focus {
    border-color: #E8845A;
    box-shadow: 0 0 0 3px rgba(232,132,90,0.15);
  }
  .btn-submit {
    width: 100%;
    background: #E8845A;
    color: #fff;
    font-family: inherit;
    font-size: 1rem;
    font-weight: 600;
    padding: 16px;
    border-radius: 50px;
    border: none;
    cursor: pointer;
    margin-top: 8px;
    min-height: 52px;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s;
  }
  .btn-submit:hover:not(:disabled) {
    transform: scale(1.02);
    background: #d9724a;
    box-shadow: 0 6px 20px rgba(232,132,90,0.4);
  }
  .btn-submit:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  .error-msg {
    background: #FFF0EE;
    border: 1px solid #F5C0B4;
    color: #C0392B;
    border-radius: 10px;
    padding: 12px 16px;
    font-size: 0.875rem;
    margin-bottom: 20px;
    line-height: 1.5;
  }
  .auth-footer {
    text-align: center;
    margin-top: 28px;
    font-size: 0.875rem;
    color: #666;
  }
  .auth-footer a {
    color: #E8845A;
    font-weight: 600;
    text-decoration: none;
    margin-left: 5px;
    transition: color 0.2s;
  }
  .auth-footer a:hover { color: #d9724a; }
  @media (max-width: 480px) {
    .auth-card { padding: 36px 24px; }
  }
`;

export default function Login() {
  const { t } = useLang();
  const l = t.login;
  const navigate = useNavigate();
  useFavicon('public');  // public-facing surface
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(res.status === 401 ? l.errors.invalid : l.errors.generic);
      } else {
        localStorage.setItem('am_token', data.token);
        localStorage.setItem('am_user', JSON.stringify(data.user));
        navigate('/portal');
      }
    } catch {
      setError(l.errors.generic);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <style>{styles}</style>
      <div className="auth-page">
        <Link to="/" className="auth-logo" aria-label="Amigo Mio home">
          <img src="/logo/logo-full-watercolor.png" alt="Amigo Mio — AI Voice Companion" />
        </Link>
        <LanguageSwitcher />
        <div className="auth-card">
          <h1 className="auth-title">{l.title}</h1>
          {error && <div className="error-msg">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">{l.email}</label>
              <input
                className="form-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{l.password}</label>
              <input
                className="form-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
            <button className="btn-submit" type="submit" disabled={loading}>
              {loading ? '…' : l.submit}
            </button>
          </form>
          <p className="auth-footer">
            {l.noAccount}
            <Link to="/signup">{l.signUp}</Link>
          </p>
        </div>
      </div>
    </>
  );
}
